import React, {useCallback, useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {AutoComplete} from '@optimaxdev/design-system';
import {Icon, Search} from '@optimaxdev/icons';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import classNames from 'classnames';

import {TYP_TYP_FORM_ACTION} from 'constants/analytics/events/typ';
import {DOCTOR_DETAILS_DEFAULT} from 'constants/product';
import {DoctorDetailsAndUploadValidatedType} from 'desktop/pages/success/types/orderItemsTypes';

import {useGetAlgoliaDoctorsDetails} from 'hooks/success/useGetAlgoliaDoctorsDetails/useGetAlgoliaDoctorsDetails';
import {DoctorDetailsType, FieldType} from 'types/successPage';

import s from '../drDetailsRedesign.scss';
import {NoResultComponent, SelectedComponent, Suggestion} from '../suggestion/suggestion';

import {useAutocompleteDrDetails} from './helper/useAutocompleteDrDetails';

const autocompleteFieldLabel = 'Find a Doctor or Clinic';

export type PropsType = {
    isModalOpen: boolean | null;
    selectedSuggestionId: string | null;
    orderItemId: string;
    doctorDetails: DoctorDetailsType;
    error: string;
    changeField: ActionCreatorWithPayload<FieldType, string>;
    setErrors: ActionCreatorWithPayload<DoctorDetailsAndUploadValidatedType, string>;
    setSelectedSuggestionId: (id: string | null) => void;
    toggleAutocomplete: () => void;
};

/**
 * This component render only dr details when customer chooses fill online
 * @param {PropsType} props - props type
 * @returns {React.ReactElement} Rendered react component
 */
export const AutocompleteDrDetails: React.FC<PropsType> = ({
    changeField,
    doctorDetails,
    error,
    isModalOpen,
    orderItemId,
    selectedSuggestionId,
    setErrors,
    setSelectedSuggestionId,
    toggleAutocomplete,
}) => {
    const {
        autoCompleteValue,
        handleClickOutside,
        inputContainerRef,
        inputRef,
        isPreviewVisible,
        setAutoCompleteValue,
        setPreviewVisibility,
    } = useAutocompleteDrDetails();

    const {suggestions} = useGetAlgoliaDoctorsDetails(autoCompleteValue);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    useEffect(() => {
        if (isModalOpen === false && autoCompleteValue === '') setPreviewVisibility(false);
    }, [isModalOpen, autoCompleteValue, setPreviewVisibility]);

    /**
     * sets selected dr to store
     * @param {string | null} suggestionId - selected item id
     */
    const selectHandler = useCallback(
        (suggestionId: string | null) => {
            setSelectedSuggestionId(suggestionId);
            const selectedSuggestion = suggestions?.find(
                suggestion => suggestion.id === suggestionId,
            ) as Partial<DoctorDetailsType>;
            if (selectedSuggestion) {
                sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {label: 'Select', version: 'D'});

                changeField({
                    doctorDetails: {
                        ...doctorDetails,
                        ...selectedSuggestion,
                        city: selectedSuggestion.mailingAddressCity,
                        name: `${selectedSuggestion.firstName} ${selectedSuggestion.lastName}`,
                        regionCode: selectedSuggestion.mailingAddressState,
                    },
                    orderItemId,
                } as FieldType);

                setErrors({
                    [orderItemId]: DOCTOR_DETAILS_DEFAULT,
                });
            } else {
                sendDesktopAnalyticsEvent(TYP_TYP_FORM_ACTION, {label: 'Remove', version: 'D'});
                changeField({
                    doctorDetails: {
                        ...DOCTOR_DETAILS_DEFAULT,
                        patientName: doctorDetails.patientName,
                    },
                    orderItemId,
                } as FieldType);
            }
        },
        [setSelectedSuggestionId, suggestions, changeField, orderItemId, doctorDetails, setErrors],
    );

    return (
        <>
            {!isPreviewVisible || selectedSuggestionId ? (
                <div className={s.input} ref={inputContainerRef}>
                    <AutoComplete
                        inputOptions={{
                            isError: Boolean(error),
                            label: autocompleteFieldLabel,
                            name: 'drNameOrAddress',
                            ref: inputRef,
                        }}
                        debounceOnChange={300}
                        inputOnChange={setAutoCompleteValue}
                        noResultClickHandler={() => toggleAutocomplete()}
                        noResultComponent={NoResultComponent}
                        onSelect={selectHandler}
                        selectedSuggestion={SelectedComponent(suggestions, selectedSuggestionId)}
                        suggestionComponent={Suggestion}
                        suggestions={suggestions}
                        valueLengthToOpenResults={2}
                    />
                </div>
            ) : (
                <div
                    className={classNames(s.autocompletePreview, {
                        [s.autocompletePreviewError]: error,
                    })}
                    onClick={() => {
                        setPreviewVisibility(false);
                    }}
                    role="presentation"
                >
                    <Icon icon={Search} size="small" />
                    <p className={s.autocompletePreviewText}>{autocompleteFieldLabel}</p>
                </div>
            )}
            <div className={s.description}>
                {error ? (
                    <p className={s.error}>*{error}</p>
                ) : (
                    <p>* Search by name, state, ZIP code or city</p>
                )}
            </div>
        </>
    );
};
